// pages/TiposComunicacion.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Divider,
  Card
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BlockOutlined,
  UnlockOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  ExportOutlined,
  PhoneOutlined,
  MessageOutlined,
  MailOutlined,
  CustomerServiceOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;

const TiposComunicacion = () => {
  // Estados
  const [tiposComunicacion, setTiposComunicacion] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentTipo, setCurrentTipo] = useState(null);
  const [form] = Form.useForm();
  const [tableLoading, setTableLoading] = useState(false);
  
  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Cargar datos iniciales
  useEffect(() => {
    fetchTiposComunicacion();
  }, []);

  const fetchTiposComunicacion = async () => {
    setTableLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/tipos-comunicacion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Asegurar que todos los registros tengan campo Activo
      const data = response.data.data.map(item => ({
        ...item,
        Activo: item.Activo === true || item.Activo === 1 || item.Activo === '1'
      }));
      
      setTiposComunicacion(data);
      setFilteredData(data);
      
      console.log('✅ Tipos de comunicación cargados:', data);
    } catch (error) {
      message.error('Error al cargar los tipos de comunicación');
      console.error('Error fetchTiposComunicacion:', error);
    } finally {
      setTableLoading(false);
    }
  };

  // Función para búsqueda global
  const handleGlobalSearch = (searchText) => {
    setGlobalSearchText(searchText);
    
    if (!searchText.trim()) {
      setFilteredData(tiposComunicacion);
      return;
    }
    
    const lowerSearchText = searchText.toLowerCase().trim();
    
    const filtered = tiposComunicacion.filter(record => {
      return Object.keys(record).some(key => {
        const value = record[key];
        
        if (value === null || value === undefined) return false;
        
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(lowerSearchText);
      });
    });
    
    setFilteredData(filtered);
  };

  // Función para obtener el icono según el tipo de comunicación
  const getIconForTipo = (descripcion) => {
    const descLower = descripcion.toLowerCase();
    if (descLower.includes('teléfono') || descLower.includes('telefono')) {
      return <PhoneOutlined style={{ color: '#1890ff' }} />;
    } else if (descLower.includes('whatsapp')) {
      return <MessageOutlined style={{ color: '#25D366' }} />;
    } else if (descLower.includes('correo') || descLower.includes('email')) {
      return <MailOutlined style={{ color: '#FF4D4F' }} />;
    } else if (descLower.includes('recepción') || descLower.includes('recepcion')) {
      return <CustomerServiceOutlined style={{ color: '#52c41a' }} />;
    }
    return <PhoneOutlined />;
  };

  // Configuración de columnas
  const columns = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 80,
      sorter: (a, b) => a.Codigo - b.Codigo,
      sortOrder: sortedInfo.columnKey === 'Codigo' && sortedInfo.order,
      render: (codigo) => <Tag color="blue">#{codigo}</Tag>,
    },
    {
      title: 'Icono',
      key: 'icono',
      width: 60,
      render: (_, record) => getIconForTipo(record.Descripcion),
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      width: 200,
      sorter: (a, b) => a.Descripcion.localeCompare(b.Descripcion),
      sortOrder: sortedInfo.columnKey === 'Descripcion' && sortedInfo.order,
      ellipsis: true,
    },
    {
      title: 'Estado',
      dataIndex: 'Activo',
      key: 'Activo',
      width: 100,
      render: (activo) => (
        <Badge
          status={activo ? 'success' : 'error'}
          text={activo ? 'Activo' : 'Inactivo'}
        />
      ),
      filters: [
        { text: 'Activos', value: true },
        { text: 'Inactivos', value: false },
      ],
      filteredValue: filteredInfo.Activo || null,
      onFilter: (value, record) => record.Activo === value,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          {/* <Tooltip title={record.Activo ? "Desactivar" : "Activar"}>
            <Button
              type={record.Activo ? "dashed" : "primary"}
              danger={record.Activo}
              icon={record.Activo ? <BlockOutlined /> : <UnlockOutlined />}
              size="small"
              onClick={() => handleToggleActive(record)}
            />
          </Tooltip> */}
          
          <Popconfirm
            title="¿Eliminar tipo de comunicación?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.Codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
          
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Handlers para las acciones
  const handleCreate = () => {
    setModalType('create');
    setCurrentTipo(null);
    form.resetFields();
    form.setFieldsValue({
      Activo: true,
      Descripcion: ''
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentTipo(record);
    
    form.setFieldsValue({
      ...record,
      Activo: record.Activo || false,
      Descripcion: record.Descripcion || ''
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    Modal.info({
      title: `Detalle de Tipo de Comunicación: ${record.Descripcion}`,
      width: 600,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <strong>Código:</strong> <Tag color="blue">#{record.Codigo}</Tag>
            </Col>
            <Col span={8}>
              <strong>Estado:</strong> {record.Activo ? 'ACTIVO' : 'INACTIVO'}
            </Col>
            <Col span={8}>
              <strong>Icono:</strong> {getIconForTipo(record.Descripcion)}
            </Col>
            <Col span={24}>
              <Divider orientation="left">Descripción</Divider>
            </Col>
            <Col span={24}>
              <Card size="small">
                <Text>{record.Descripcion}</Text>
              </Card>
            </Col>
            <Col span={24}>
              <Divider orientation="left">Información Adicional</Divider>
            </Col>
            <Col span={24}>
              <Text type="secondary">
                {getUsageDescription(record.Descripcion)}
              </Text>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const getUsageDescription = (descripcion) => {
    const descLower = descripcion.toLowerCase();
    if (descLower.includes('teléfono') || descLower.includes('telefono')) {
      return 'Este tipo se utiliza para contactos telefónicos directos con los clientes.';
    } else if (descLower.includes('whatsapp')) {
      return 'Comunicación a través de WhatsApp para mensajería instantánea.';
    } else if (descLower.includes('correo') || descLower.includes('email')) {
      return 'Correo electrónico para comunicaciones formales y documentación.';
    } else if (descLower.includes('recepción') || descLower.includes('recepcion')) {
      return 'Comunicación en persona en la recepción del hotel.';
    }
    return 'Tipo de comunicación general.';
  };

  const handleToggleActive = async (record) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      const nuevoEstado = !record.Activo;
      
      const response = await axios.patch(
        `${BASE_URL}/api/tipos-comunicacion/${record.Codigo}/estado`,
        { Activo: nuevoEstado },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const tipoActualizado = {
        ...response.data.data,
        Activo: response.data.data.Activo === true || response.data.data.Activo === 1 || response.data.data.Activo === '1'
      };
      
      setTiposComunicacion(tiposComunicacion.map(t => 
        t.Codigo === record.Codigo ? tipoActualizado : t
      ));
      
      setFilteredData(prev => prev.map(t => 
        t.Codigo === record.Codigo ? tipoActualizado : t
      ));
      
      message.success(`Tipo de comunicación ${nuevoEstado ? 'activado' : 'desactivado'} correctamente`);
      
    } catch (error) {
      message.error('Error al actualizar el estado');
      console.error('Error handleToggleActive:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleDelete = async (codigo) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${BASE_URL}/api/tipos-comunicacion/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const nuevosTipos = tiposComunicacion.filter(t => t.Codigo !== codigo);
      setTiposComunicacion(nuevosTipos);
      
      const nuevoFilteredData = filteredData.filter(t => t.Codigo !== codigo);
      setFilteredData(nuevoFilteredData);
      
      message.success('Tipo de comunicación eliminado correctamente');
      
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar el tipo de comunicación');
      console.error('Error handleDelete:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      console.log('📝 Datos a guardar:', values);
      
      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/tipos-comunicacion`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const nuevoTipo = {
          ...response.data.data,
          Activo: response.data.data.Activo === true || response.data.data.Activo === 1 || response.data.data.Activo === '1'
        };
        
        setTiposComunicacion([...tiposComunicacion, nuevoTipo]);
        
        if (globalSearchText) {
          const lowerSearchText = globalSearchText.toLowerCase();
          const shouldShowInSearch = Object.keys(nuevoTipo).some(key => {
            const value = nuevoTipo[key];
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(lowerSearchText);
          });
          
          if (shouldShowInSearch) {
            setFilteredData([...filteredData, nuevoTipo]);
          }
        } else {
          setFilteredData([...filteredData, nuevoTipo]);
        }
        
        message.success('Tipo de comunicación creado correctamente');
        
      } else {
        const response = await axios.put(
          `${BASE_URL}/api/tipos-comunicacion/${currentTipo.Codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const tipoActualizado = {
          ...response.data.data,
          Activo: response.data.data.Activo === true || response.data.data.Activo === 1 || response.data.data.Activo === '1'
        };
        
        const nuevosTipos = tiposComunicacion.map(t => 
          t.Codigo === currentTipo.Codigo ? tipoActualizado : t
        );
        
        setTiposComunicacion(nuevosTipos);
        
        const nuevoFilteredData = filteredData.map(t => 
          t.Codigo === currentTipo.Codigo ? tipoActualizado : t
        );
        
        setFilteredData(nuevoFilteredData);
        
        message.success('Tipo de comunicación actualizado correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error en handleSave:', error);
      
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.response?.data?.details) {
        message.error(error.response.data.details);
      } else {
        message.error('Error al guardar los datos');
      }
    } finally {
      setLoading(false);
    }
  };
  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    let result = [...tiposComunicacion];
    
    Object.keys(filters).forEach(key => {
      const filterValues = filters[key];
      if (filterValues && filterValues.length > 0) {
        result = result.filter(item => {
          const itemValue = item[key];
          
          if (key === 'Activo') {
            return filterValues.includes(itemValue);
          }
          
          return true;
        });
      }
    });
    
    if (sorter.field && sorter.order) {
      result.sort((a, b) => {
        const aVal = a[sorter.field] || '';
        const bVal = b[sorter.field] || '';
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          if (sorter.order === 'ascend') {
            return aVal.localeCompare(bVal);
          } else {
            return bVal.localeCompare(aVal);
          }
        } else {
          if (sorter.order === 'ascend') {
            return (aVal || 0) - (bVal || 0);
          } else {
            return (bVal || 0) - (aVal || 0);
          }
        }
      });
    }
    
    if (globalSearchText) {
      const lowerSearchText = globalSearchText.toLowerCase().trim();
      result = result.filter(record => {
        return Object.keys(record).some(key => {
          const value = record[key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(lowerSearchText);
        });
      });
    }
    
    setFilteredData(result);
  };

  const clearFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setGlobalSearchText('');
    setFilteredData(tiposComunicacion);
    message.success('Filtros y búsqueda limpiados');
  };

  // Función para exportar
  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Tipos" 
          value={tiposComunicacion.length}
          prefix={<PhoneOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Activos" 
          value={tiposComunicacion.filter(t => t.Activo).length}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Digitales" 
          value={tiposComunicacion.filter(t => 
            t.Descripcion.toLowerCase().includes('whatsapp') || 
            t.Descripcion.toLowerCase().includes('correo')
          ).length}
          prefix={<MessageOutlined />}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Directos" 
          value={tiposComunicacion.filter(t => 
            t.Descripcion.toLowerCase().includes('teléfono') || 
            t.Descripcion.toLowerCase().includes('telefono') ||
            t.Descripcion.toLowerCase().includes('recepción') ||
            t.Descripcion.toLowerCase().includes('recepcion')
          ).length}
          prefix={<CustomerServiceOutlined />}
          valueStyle={{ color: '#fa8c16' }}
        />
      </Col>
    </Row>
  );

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input.Search
          placeholder="Buscar por código, descripción..."
          value={globalSearchText}
          style={{ width: 400 }}
          allowClear
          enterButton
          onChange={(e) => {
            setGlobalSearchText(e.target.value);
            if (e.target.value === '') {
              handleGlobalSearch('');
            }
          }}
          onSearch={handleGlobalSearch}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={clearFilters}
          size="small"
        >
          Limpiar
        </Button>
        <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
          <strong>{filteredData.length}</strong> registros
        </Tag>
      </Space>
    </div>
  );

  return (
    <>
      <PageContainer
        title="Gestión de Tipos de Comunicación"
        subtitle={`${tiposComunicacion.length} tipos registrados`}
        icon={<PhoneOutlined />}
        loading={tableLoading}
        onRefresh={fetchTiposComunicacion}
        onCreate={handleCreate}
        onExport={handleExport}
        badgeCount={tiposComunicacion.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{tiposComunicacion.length}</strong> tipos
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}

        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="Codigo"
          loading={tableLoading}
          onChange={handleChange}
          scroll={{ x: 1200 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar */}
      <Modal
        title={modalType === 'create' ? 'Nuevo Tipo de Comunicación' : 'Editar Tipo de Comunicación'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="Descripcion"
                label="Descripción"
                rules={[{ required: true, message: 'Por favor ingrese la descripción' }]}
                help="Ejemplos: Teléfono, WhatsApp, Correo Electrónico, Recepción"
              >
                <Input 
                  placeholder="Descripción del tipo de comunicación" 
                  maxLength={100} 
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="Activo"
                label="Estado"
                valuePropName="checked"
              >
                <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', borderRadius: 6 }}>
            <Text type="secondary">
              <strong>💡 Información:</strong> Los tipos de comunicación se utilizan para clasificar 
              los diferentes métodos de contacto con los clientes (teléfono, email, etc.).
            </Text>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default TiposComunicacion;